<template>
  <div id="app">
    <router-view />
    <div class="footer" v-if="!isDefaultTenant">
      <div class="content">
        <custom-logo :tenant="tenant" />
      </div>
    </div>
  </div>
</template>

<script>
import router from '@/router';
import env from '@/env';
import { DEFAULT_TENANT } from '@/constants';
import CustomLogo from '@/components/CustomLogo/CustomLogo.vue';

export default {
  name: 'App',
  components: {
    CustomLogo,
  },
  computed: {
    tenant: () => router.currentRoute?.params?.tenant || DEFAULT_TENANT,
    isDefaultTenant: (vm) => vm.tenant === DEFAULT_TENANT,
    theme: (vm) => vm.$store.getters['config/get']('theme'),
  },
  async mounted() {
    if (!this.isDefaultTenant) {
      // first, load the actual SCSS theme
      // eslint-disable-next-line no-unused-expressions, prefer-template
      import('@/themes/' + this.tenant + '/theme.scss');

      // second, load parts of the theme as JSON
      // this is required to have critical values available right away
      // eslint-disable-next-line no-unused-expressions, prefer-template
      const { default: customTheme } = await import('@/themes/' + this.tenant + '/theme.json');
      this.$store.commit('config/set', {
        key: 'theme',
        value: customTheme,
      });

      /**
       * Chrome has a bug, the theme color is not applied in the dark mode
       * https://bugs.chromium.org/p/chromium/issues/detail?id=980790
       *
       * That's why we need to manually adjust the default media element
       * and store the theme in the store
       */
      if (customTheme?.colors?.primaryDimmed) {
        if (customTheme.colors.primaryDimmed?.light) {
          document.querySelector('meta[name="theme-color"][media="(prefers-color-scheme: light)"]')
            .content = customTheme.colors.primaryDimmed.light;
        }
        if (customTheme.colors.primaryDimmed?.dark) {
          document.querySelector('meta[name="theme-color"][media=""]')
            .content = customTheme.colors.primaryDimmed.dark;
          document.querySelector('meta[name="theme-color"][media="(prefers-color-scheme: dark)"]')
            .content = customTheme.colors.primaryDimmed.dark;
        }
      }
    }

    if (window.matchMedia) {
      // watch changes on the scheme preference
      window.matchMedia('(prefers-color-scheme: dark)')
        .addEventListener('change', (event) => {
          const colorScheme = event.matches ? 'dark' : 'light';
          this.checkColorScheme(colorScheme);
          this.$store.commit('config/set', {
            key: 'colorScheme',
            value: colorScheme,
          });
        });

      // detect the initial scheme and make the initial set
      const initialColorScheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      this.checkColorScheme(initialColorScheme);
    }
  },
  methods: {
    checkColorScheme(scheme) {
      /**
       * swap theme color, mainly for the CircleStage component
       *
       * The decision to do that here in the App.vue is that it accesses a global property which
       * should not be done by a component. However, if you disagree, you may want to move it to
       * the CircleStage and put it behind a property switch
       */
      if (this.theme?.colors?.primaryDimmed?.[scheme]) {
        // the following approach was too slow for the initial (empty at .mounted)
        // that's why we require the `theme.json`
        // getComputedStyle(document.documentElement).getPropertyValue(--colors-primary-dimmed);
        document.querySelector('meta[name="theme-color"][media=""]')
          .content = this.theme.colors.primaryDimmed[scheme];
      }

      // swap favicon
      const faviconPath = env.BASE_URL + (scheme === 'light') ? 'faviconLight.svg' : 'favicon.svg';
      document.querySelector('link[rel="icon"][type="image/svg+xml"]:not([media])').href = faviconPath;
    },
  },
};
</script>

<style lang="scss">
@import "@/styles/theme.scss";

html,
body {
  height: 100%;
  height: 100dvh;
}
body {
  display: flex;
  margin: 0;
  font-family: var(--font-regular-family);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--colors-main-font);
  background-color: var(--colors-main-background);
}
@supports (-webkit-touch-callout: none) {
  html,
  body {
    height: 100%;
    height: -webkit-fill-available;
  }
}

#app {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
}

a {
  text-decoration: none;
  padding-bottom: 2px;
  color: var(--colors-main-font);
  border-bottom: 1px solid currentColor;

  &:hover,
  &:focus {
    color: var(--colors-primary);
  }

  &:focus {
    border-bottom: 1px solid transparent;
    outline: 2px solid currentColor;
    border-radius: 2px;
  }
}
</style>

<style lang="scss" scoped>
.footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: var(--spacing-side);
  padding: 0px var(--spacing-side) var(--spacing-side);
  text-align: center;
  box-sizing: border-box;

  &:before {
    content: "";
    display: block;
    width: 100%;
    height: 1px;
    margin-bottom: var(--spacing-side);
    background-color: var(--colors-main-font);
    opacity: .2;

    @media (prefers-color-scheme: light) {
      opacity: .1;
    }
  }

  .content {
    width: 100%;
    max-width: var(--spacing-width-max);
  }
}
</style>
